define([
    '../Util/defaultValue',
    '../Util/defined',
    '../Util/encode',
    '../Util/callbackSystem',
    '../Util/array2Json',
    '../Util/json2Array',
    '../System/ColorObj',
    '../System/Vec3Obj',
    '../Enum/EnumGeoFenceEditMode'
], function (
    defaultValue,
    defined,
    encode,
    callbackSystem,
    array2Json,
    json2Array,
    ColorObj,
    Vec3Obj,
    EnumGeoFenceEditMode
) {
    'use strict';

    /**
     * 地理围栏。
     * <br /><br />
     * <div align='center'>
     * <img src='images/GeoFence.gif' width='900' height='650' /><br />
     * 地理围栏效果图
     * </div>
     * 
     * @demo {@link http://lbs.zjkelan.com/GeoFence.html|CooWasm GeoFence demo}
     * @alias GeoFence
     * @constructor
     *
     * @param {Object} props 包含如下属性的Object：
     * @param {EnumGeoFenceEditMode} [props.editMode=EnumGeoFenceEditMode.CREATE] 编辑状态，如果points为空，则需要通过鼠标点击场景创建；如果points不为空，则为展示模式。
     * @param {Array.<Vec3Obj>} [props.points=null] 当editMode为SHOW时，必须传入相应点位构成地理围栏。
     * @param {ColorObj|String} [props.color=new ColorObj(0.0, 1.0, 1.0, 1.0)] 围栏颜色，与围栏纹理路径二选一，优先级较低。
     * @param {Number} [props.height=50.0] 围栏高度，单位：米。
     * @param {String} [props.url=null] 围栏纹理路径，与围栏颜色二选一，优先级较高。
     * @param {ColorObj|String} [props.pointColor=new ColorObj(0.0, 1.0, 0.0, 1.0)] 关键点颜色,可以是ColorObj对象，或者16进制颜色，如#FFFFFFFF。
     * @param {Number} [props.pointSize=3.0] 关键点大小。
     * @param {ColorObj|String} [props.lineColor=new ColorObj(1.0, 0.0, 0.0, 1.0)] 边框线颜色,可以是ColorObj对象，或者16进制颜色，如#FFFFFFFF。
     * @param {Number} [props.lineWidth=2.0] 边框线宽度。
     * @param {Boolean} [props.bDynamic=false] 是否动态展示。
     * @param {Boolean} [props.bHorizontal=false] 是否水平波动。
     * @param {Number} [props.blockNum=1] 当围栏为颜色模式时，控制波段数。
     * @param {Number} [props.speed=1.0] 动态展示速度。
     * @param {Number} [props.distance=20000.0] 最大显示距离，单位：米。
     * @param {Number} [props.minAltitude=0.0] 最小可见高度。
     * @param {Number} [props.maxAltitude=3.4028234663852886e+38] 最大可见高度。
     * @param {Boolean} [props.bVisible=true] 显隐状态。
     * @param {CallbackBoolean} [callback=null] 加载成功后触发的函数回调。
     * @example
     * var GeoFence = new CooWasm.GeoFence({},(state)=>{console.log("load status:" + state)});
     */
    class GeoFence {
        /**
         * @description 编辑状态，如果points为空，则需要通过鼠标点击场景创建；如果points不为空，则为展示模式。
         * @type {EnumGeoFenceEditMode}
         */
        editMode
        /**
         * @description 当editMode为SHOW时，必须传入相应点位构成地理围栏。
         * @type {Array.<Vec3Obj>}
         */
        points
        /**
         * @description 围栏颜色，与围栏纹理路径二选一，优先级较低。
         * @type {ColorObj|String}
         */
        color
        /**
         * @description 围栏高度，单位：米。
         * @type {Number}
         */
        height
        /**
         * @description 围栏纹理路径，与围栏颜色二选一，优先级较高。
         * @type {String}
         */
        url
        /**
         * @description 关键点颜色,可以是ColorObj对象，或者16进制颜色，如#FFFFFFFF。
         * @type {ColorObj|String}
         */
        pointColor
        // /**
        //  * @description 关键点大小。
        //  * @type {Number}
        //  */
        // pointSize;
        /**
         * @description 边框线颜色,可以是ColorObj对象，或者16进制颜色，如#FFFFFFFF。
         * @type {ColorObj|String}
         */
        lineColor
        /**
         * @description 边框线宽度。
         * @type {Number}
         */
        lineWidth
        /**
         * @description 是否动态展示。
         * @type {Boolean}
         */
        bDynamic
        /**
         * @description 是否水平波动。
         * @type {Boolean}
         */
        bHorizontal
        /**
         * @description 当围栏为颜色模式时，控制波段数。
         * @type {Number} 
         */
        blockNum
        /**
         * @description 动态展示速度。
         * @type {Number}
         */
        speed
        /**
         * @description 最大显示距离，单位：米。
         * @type {Number}
         */
        distance
        /**
         * @description 最小可见高度。
         * @type {Number}
         */
        minAltitude
        /**
         * @description 最大可见高度。
         * @type {Number}
         */
        maxAltitude
        /**
         * @description 显隐状态。
         * @type {Boolean}
         * @default true
         */
        bVisible = true
        /**
         * @description 地理围栏对象uuid。
         * @type {String}
         * @readonly
         */
        avatarUuid = null
        /**
         * @description 地理围栏组件uuid。
         * @type {String}
         * @readonly
         */
        componentDynamicFenceUuid = null
        /**
         * @description 高度控制组件uuid。
         * @type {String}
         * @readonly
         */
        componentAltitudeVisCtrlUuid = null
        /**
         * @description GeoFence在CooWasm中类名。
         * @private
         * @readonly
         */
        className = "GeoFence"
        static avatarType = "deSMP.Avatar.EmptyAvatar"
        static componentDynamicFenceType = "deSMP.AvatarComponent.DynamicFenceComponent"
        static componentAltitudeVisCtrlType = "deSMP.AvatarComponent.AltitudeVisCtrlComponent"
        constructor(props, callback) {
            if (!defined(props)) {
                props = {}
            }
            if (defined(props.build) && props.build == true) {
                return
            }
            let bSync = true
            //create avatars
            let avatarCategoryName = "deSMP.Avatar.EmptyAvatar"
            let componentCategoryName = GeoFence.componentDynamicFenceType + "#$%" + GeoFence.componentAltitudeVisCtrlType + "#$%"
            let avatarProps = ""
            let componentProps = ""
            //avatar props
            avatarProps += this.toStr("bVisible", props.bVisible, "Visible")
            //component props
            let dynamicFenceComponentProps = ""
            dynamicFenceComponentProps += this.toStr("editMode", props.editMode, "EditMode")
            if (defined(props.points)) {
                this.points = props.points
                dynamicFenceComponentProps += "Points:" + array2Json("TUPLE3D", props.points) + "|"
            }
            if (defined(props.url)) {
				this.url = props.url
                dynamicFenceComponentProps += this.toStr("url", props.url, "ImageUrl")
                dynamicFenceComponentProps += "DrawMode:TEXTURE|"
            } else {
                dynamicFenceComponentProps += "DrawMode:COLOR|"
            }
            dynamicFenceComponentProps += this.toColorStr("color", props.color, "PolygonColor")
            dynamicFenceComponentProps += this.toStr("height", props.height, "PolygonHeight")
            dynamicFenceComponentProps += this.toColorStr("pointColor", props.pointColor, "PointColor")
            dynamicFenceComponentProps += this.toStr("pointSize", props.pointSize, "PointSize")
            dynamicFenceComponentProps += this.toColorStr("lineColor", props.lineColor, "LineColor")
            dynamicFenceComponentProps += this.toStr("lineWidth", props.lineWidth, "LineWidth")
            dynamicFenceComponentProps += this.toStr("bDynamic", props.bDynamic, "IsDynamic")
            dynamicFenceComponentProps += this.toStr("bHorizontal", props.bHorizontal, "IsHorizontal")
            dynamicFenceComponentProps += this.toStr("blockNum", props.blockNum, "BlockNum")
            dynamicFenceComponentProps += this.toStr("speed", props.speed, "Speed")
            dynamicFenceComponentProps += this.toStr("distance", props.distance, "Distance")
            dynamicFenceComponentProps += "Active:1|"
            componentProps += dynamicFenceComponentProps + "#$%"
            let altitudeVisCtrlComponentProps = ""
            altitudeVisCtrlComponentProps += this.toStr("minAltitude", props.minAltitude, "MinVisibleAltitude")
            altitudeVisCtrlComponentProps += this.toStr("maxAltitude", props.maxAltitude, "MaxVisibleAltitude")
            componentProps += altitudeVisCtrlComponentProps + "#$%"
            var idInfo = Element.prototype.NativeAPI.LoadAvatarAndMultiComponent(encode(avatarCategoryName), encode(avatarProps), encode(componentCategoryName), encode(componentProps), bSync, this.bVisible, true)
            // var idInfo = Element.prototype.NativeAPI.LoadAvatarAndComponent(encode(""), aCategory, aName, encode(aProps), cCategory, cName, encode(cProps), this.bVisible, true);
            if (idInfo != "") {
                var idArray = idInfo.split('#$%')
                this.avatarUuid = idArray[0]
                this.componentDynamicFenceUuid = idArray[1]
                this.componentAltitudeVisCtrlUuid = idArray[2]
				callbackSystem.register("LoadAvatarAndComponent", this.avatarUuid, (state) => {
					this.getProperties()
					if (defined(callback) && defined(this.points) && this.points.length > 0) {
						callback(state)
					}
					Element.prototype.Map.geoFences.push(this)
				})
            }
        }

        /**
         * <p>
         * 更新单个或者多个对象属性，使之立即生效。
         * </p>
         *
         * @param {Object} [props] 包含如下属性的Object。
         * @param {EnumGeoFenceEditMode} [props.editMode] 编辑状态，如果points为空，则需要通过鼠标点击场景创建；如果points不为空，则为展示模式。
         * @param {Array.<Vec3Obj>} [props.points] 当editMode为SHOW时，必须传入相应点位构成地理围栏。
         * @param {ColorObj|String} [props.color] 围栏颜色，与围栏纹理路径二选一，优先级较低。
         * @param {Number} [props.height] 围栏高度，单位：米。
         * @param {String} [props.url] 围栏纹理路径，与围栏颜色二选一，优先级较高。
         * @param {ColorObj|String} [props.pointColor] 关键点颜色,可以是ColorObj对象，或者16进制颜色，如#FFFFFFFF。
         * @param {Number} [props.pointSize] 关键点大小。
         * @param {ColorObj|String} [props.lineColor] 边框线颜色,可以是ColorObj对象，或者16进制颜色，如#FFFFFFFF。
         * @param {Number} [props.lineWidth] 边框线宽度。
         * @param {Boolean} [props.bDynamic] 是否动态展示。
         * @param {Boolean} [props.bHorizontal] 是否水平波动。
         * @param {Number} [props.blockNum] 当围栏为颜色模式时，控制波段数。
         * @param {Number} [props.speed] 动态展示速度。
         * @param {Number} [props.distance] 最大显示距离，单位：米。
         * @param {Number} [props.minAltitude] 最小可见高度。
         * @param {Number} [props.maxAltitude] 最大可见高度。
         * @param {Boolean} [props.bVisible] 显隐状态。
         * @param {CallbackBoolean} [callback] 加载成功后触发的函数回调。
         * @example
         * GeoFence.update({
         *    bVisible:false
         * },(state)=>{console.log("update status:" + state)});
         */
        update(props, callback) {
            let avatarProps = ""
            let componentProps = ""
            let dynamicFenceComponentProps = ""
            let altitudeVisCtrlComponentProps = ""
            let uuids = ""
            if (defined(props)) {
                avatarProps += this.toStr("bVisible", props.bVisible, "Visible")
                //dynamicFenceComponent
                dynamicFenceComponentProps += this.toStr("editMode", props.editMode, "EditMode")
                if (defined(props.points)) {
                    this.points = props.points
                    dynamicFenceComponentProps += "Points:" + array2Json("TUPLE3D", props.points) + "|"
                }
                if (defined(props.url) || defined(this.url)) {
					if(defined(props.url))
						this.url = props.url
                    dynamicFenceComponentProps += this.toStr("url", this.url, "ImageUrl")
                    dynamicFenceComponentProps += "DrawMode:TEXTURE|"
                } else {
                    dynamicFenceComponentProps += "DrawMode:COLOR|"
                }
                dynamicFenceComponentProps += this.toColorStr("color", props.color, "PolygonColor")
                dynamicFenceComponentProps += this.toStr("height", props.height, "PolygonHeight")
                dynamicFenceComponentProps += this.toColorStr("pointColor", props.pointColor, "PointColor")
                dynamicFenceComponentProps += this.toStr("pointSize", props.pointSize, "PointSize")
                dynamicFenceComponentProps += this.toColorStr("lineColor", props.lineColor, "LineColor")
                dynamicFenceComponentProps += this.toStr("lineWidth", props.lineWidth, "LineWidth")
                dynamicFenceComponentProps += this.toStr("bDynamic", props.bDynamic, "IsDynamic")
                dynamicFenceComponentProps += this.toStr("bHorizontal", props.bHorizontal, "IsHorizontal")
                dynamicFenceComponentProps += this.toStr("blockNum", props.blockNum, "BlockNum")
                dynamicFenceComponentProps += this.toStr("speed", props.speed, "Speed")
                dynamicFenceComponentProps += this.toStr("distance", props.distance, "Distance")
                dynamicFenceComponentProps += "Active:1|"

                //altitudeVisCtrlComponent
                altitudeVisCtrlComponentProps += this.toStr("minAltitude", props.minAltitude, "MinVisibleAltitude")
                altitudeVisCtrlComponentProps += this.toStr("maxAltitude", props.maxAltitude, "MaxVisibleAltitude")

                uuids += this.componentDynamicFenceUuid + "#$%"
                uuids += this.componentAltitudeVisCtrlUuid + "#$%"
                componentProps += dynamicFenceComponentProps + "#$%"
                componentProps += altitudeVisCtrlComponentProps + "#$%"

                if (props.editMode == EnumGeoFenceEditMode.SHOW) {
                    callbackSystem.register("UpdateAvatarAndMultiComponent", this.avatarUuid, (state) => {
                        this.getProperties()
                        if (defined(callback)) {
                            callback(state)
                        }
                    })
                } else {
                    callbackSystem.register("EditEnd", this.avatarUuid, (state) => {
                        this.getProperties()
                        if (defined(callback)) {
                            callback(state)
                        }
                    })
                }
                Element.prototype.NativeAPI.UpdateAvatarAndMultiComponent(encode(this.avatarUuid), encode(uuids), encode(avatarProps), encode(componentProps), defined(callback))
                // Element.prototype.NativeAPI.UpdateAvatarAndComponent(encode(this.avatarUuid), encode(this.componentDynamicFenceUuid), encode(avatarProps), encode(componentProps), defined(callback));
            }
        }

        toStr(memName, value, key) {
            let result = ""
            if (defined(value)) {
                this[memName] = value
                if (typeof value.toStr === 'function') {
                    result = key + ":" + value.toStr() + "|"
                } else {
                    result = key + ":" + value + "|"
                }
            }
            return result
        }
        toColorStr(memName, value, key){
            let result = "";
            if (defined(value)) {
                this[memName] = value;
                if (typeof value.toStr === 'function') {
                    result = key + ":" + value.toHexStr() + "|";
                } else {
                    if (typeof value === 'string') {
                        let color = ColorObj.fromCssColorString(value);
                        if (defined(color)) {
                            value = color.toHexStr();
                            this[memName] = value;
                            result = key + ":" + value + "|";
                        } else {
                            console.warn(`Invalid color value: ${value}. Using default value.`);
                        }
                    }
                }
            }
            return result;
        }
        /**
         * 从场景中定位自身(自动根据数据包围盒定位，不可修改定位参数)。
         * @param {CallbackBoolean} [callback=null] 飞行结束后触发的函数回调。
         * @example
         * GeoFence.locate((state)=>{console.log("locate status:" + state)});
         */
        locate(callback) {
            callbackSystem.register("", "LocateAvatar", (state) => {
                if (defined(callback)) {
                    callback(state)
                }
            })
            Element.prototype.NativeAPI.LocateAvatar(encode(this.avatarUuid))
        }

        /**
         * 获取对象包含的属性。
         * @returns {Boolean} 是否获取成功。获取成功时，会自动赋值给属性。
         * @example
         * GeoFence.getProperties();
         */
        getProperties() {
            if (!defined(this.avatarUuid)) {
                console.log("uuid require!")
                return false
            }
            let pAvatarUuid = Element.prototype.NativeAPI.Uuid_New()
            Element.prototype.NativeAPI.Uuid_SetId(pAvatarUuid, encode(this.avatarUuid))
            let pAvatar = Element.prototype.NativeAPI.MapImpl_getAvatar(pAvatarUuid)
            let avatarPropSize = Element.prototype.NativeAPI.Avatar_GetPropertiesSize(pAvatar)
            for (let i = 0; i < avatarPropSize; ++i) {
                let pProp = Element.prototype.NativeAPI.Avatar_GetProperty(pAvatar, i)
                let name = Element.prototype.NativeAPI.Property_GetName(pProp)
                let value = Element.prototype.NativeAPI.Property_GetValue(pProp)
                if (name == "Visible") {
                    if (value == "true" || value == "1") {
                        this.bVisible = true
                    } else {
                        this.bVisible = false
                    }
                }
            }
            let avatarAcSize = Element.prototype.NativeAPI.Avatar_GetAvatarComponentsSize(pAvatar)
            for (let j = 0; j < avatarAcSize; ++j) {
                let pAc = Element.prototype.NativeAPI.Avatar_GetAvatarComponent(pAvatar, j)
                let propSize = Element.prototype.NativeAPI.AvatarComponent_GetPropertiesSize(pAc)
                let pObjType = Element.prototype.NativeAPI.AvatarComponent_GetObjectType(pAc)
                let pName = Element.prototype.NativeAPI.ObjectType_GetName(pObjType)
                let pUuid = Element.prototype.NativeAPI.AvatarComponent_GetUuid(pAc)
                let uuid = Element.prototype.NativeAPI.Uuid_GetId(pUuid)
                if (pName == "DynamicFenceComponent") {
                    this.componentDynamicFenceUuid = uuid
                    for (let z = 0; z < propSize; ++z) {
                        let pProp = Element.prototype.NativeAPI.AvatarComponent_GetProperty(pAc, z)
                        let name = Element.prototype.NativeAPI.Property_GetName(pProp)
                        let value = Element.prototype.NativeAPI.Property_GetValue(pProp)
                        if (name == "EditMode") {
                            this.editMode = value
                        } else if (name == "Points") {
                            this.points = json2Array(value)
                        } else if (name == "PolygonColor") {
                            this.color = CooWasm.ColorObj.fromCssColorString(value)
                        } else if (name == "PolygonHeight") {
                            this.height = Number(value)
                        } else if (name == "ImageUrl") {
                            this.url = String(value)
                        } else if (name == "PointColor") {
                            this.pointColor = CooWasm.ColorObj.fromCssColorString(value)
                        } else if (name == "PointSize") {
                            this.pointSize = Number(value)
                        } else if (name == "LineColor") {
                            this.lineColor = CooWasm.ColorObj.fromCssColorString(value)
                        } else if (name == "LineWidth") {
                            this.lineWidth = Number(value)
                        } else if (name == "IsDynamic") {
                            if (value == "true" || value == "1") {
                                this.bDynamic = true
                            } else {
                                this.bDynamic = false
                            }
                        } else if (name == "IsHorizontal") {
                            if (value == "true" || value == "1") {
                                this.bHorizontal = true
                            } else {
                                this.bHorizontal = false
                            }
                        } else if (name == "BlockNum") {
                            this.blockNum = Number(value)
                        } else if (name == "Speed") {
                            this.speed = Number(value)
                        } else if (name == "Distance") {
                            this.distance = Number(value)
                        }
                    }
                } else if (pName == "AltitudeVisCtrlComponent") {
                    this.componentAltitudeVisCtrlUuid = uuid
                    for (let z = 0; z < propSize; ++z) {
                        let pProp = Element.prototype.NativeAPI.AvatarComponent_GetProperty(pAc, z)
                        let name = Element.prototype.NativeAPI.Property_GetName(pProp)
                        let value = Element.prototype.NativeAPI.Property_GetValue(pProp)
                        if (name == "MinVisibleAltitude") {
                            this.minAltitude = Number(value)
                        } else if (name == "MaxVisibleAltitude") {
                            this.maxAltitude = Number(value)
                        }
                    }
                }
            }
            Element.prototype.NativeAPI.Avatar_Delete(pAvatar)
            Element.prototype.NativeAPI.Uuid_Delete(pAvatarUuid)
            return true
        }
        /**
         * 从场景中销毁自身。
         * @param {CallbackBoolean} [callback=null] 销毁对象后触发的函数回调。
         * @returns null 空值。
         * @example
         * GeoFence = GeoFence.destroy((state)=>{console.log("destroy status:" + state)});
         */
        destroy(callback) {
            if (!defined(this.avatarUuid)) {
                console.log("avatarUuid is null!")
                return null
            }
            callbackSystem.register("RemoveAvatar", this.avatarUuid, (state) => {
                if (defined(callback)) {
                    callback(state)
                }
                Element.prototype.Map.geoFences = Element.prototype.Map.geoFences.filter(item => item !== this)
                callbackSystem.remove("LoadAvatarAndComponent", this.avatarUuid)
                callbackSystem.remove("UpdateAvatarAndMultiComponent", this.avatarUuid)
                callbackSystem.remove("EditEnd", this.avatarUuid)
                callbackSystem.remove("", "LocateAvatar")
                callbackSystem.remove("RemoveAvatar", this.avatarUuid)
                this.avatarUuid = null
                this.componentDynamicFenceUuid = null
                this.componentAltitudeVisCtrlUuid = null
            })
            Element.prototype.NativeAPI.RemoveAvatar(encode(this.avatarUuid))
            return null
        }
    }
    return GeoFence
})